// Contact geo-sorting module
// Sorts contacts based on user's country detection

class ContactGeoSorter {
    constructor() {
        this.init();
    }

    async init() {
        const container = document.querySelector('[data-geosort="enabled"]');
        if (!container) return;

        try {
            const userCountry = await this.getUserCountry();
            if (userCountry) {
                this.sortContacts(container, userCountry);
            }
        } catch (error) {
            // Silently fail and keep default order
        }
    }

    async getUserCountry() {
        try {
            const ip = await this.getPublicIP();
            const country = await this.getCountryFromIP(ip);

            if (country) {
                return country;
            }
        } catch (error) {
            // Silently fail
        }

        return null;
    }

    async getPublicIP() {
        const services = [
            'https://ipapi.co/ip/',
            'https://api.ipify.org?format=text',
            'https://ifconfig.me/ip'
        ];

        for (const service of services) {
            try {
                const response = await fetch(service, { cache: 'no-cache' });
                if (response.ok) {
                    const ip = await response.text();
                    return ip.trim();
                }
            } catch (error) {
                continue;
            }
        }
        throw new Error('Failed to get public IP');
    }

    async getCountryFromIP(ip) {
        try {
            const response = await fetch(`https://ipapi.co/${ip}/country_code/`, {
                cache: 'no-cache'
            });
            if (response.ok) {
                const countryCode = await response.text();
                return countryCode.trim();
            }
        } catch (error) {
            // Silently fail
        }
        return null;
    }

    sortContacts(container, userCountry) {
        // Check if slider is initialized and destroy it temporarily
        const $container = $(container);
        const wasSlickInitialized = $container.hasClass('slick-initialized');

        if (wasSlickInitialized) {
            $container.slick('unslick');
        }

        // Now get the real contacts (after removing Slick clones)
        const contacts = Array.from(container.querySelectorAll('.js-contact-item'));
        if (!contacts.length) return;

        // Check if any contact matches the user's country
        const hasCountryMatch = contacts.some(contact => {
            const region = contact.getAttribute('data-region') || '';
            return this.isCountryMatch(region, userCountry);
        });

        // Sort contacts by priority
        const sorted = contacts.sort((a, b) => {
            const regionA = a.getAttribute('data-region') || '';
            const regionB = b.getAttribute('data-region') || '';

            const scoreA = this.getContactScore(regionA, userCountry, hasCountryMatch);
            const scoreB = this.getContactScore(regionB, userCountry, hasCountryMatch);

            return scoreB - scoreA; // Higher score first
        });

        // Reorder DOM elements
        sorted.forEach(contact => {
            container.appendChild(contact);
        });

        // Reinitialize slider if it was previously initialized
        if (wasSlickInitialized) {
            $container.slick({
                dots: false,
                slidesToShow: 1,
                adaptiveHeight: true
            });
        }

    }

    isCountryMatch(region, userCountry) {
        // Exact country match
        if (region === userCountry) return true;

        // Handle country name variants - mapping country codes to possible region names
        const countryVariants = {
            'US': ['United States', 'USA', 'America', 'United States of America'],
            'CA': ['Canada', 'CAN'],
            'GB': ['United Kingdom', 'UK', 'Britain', 'Great Britain', 'England'],
            'DE': ['Germany', 'Deutschland', 'DEU'],
            'FR': ['France', 'FRA'],
            'IN': ['India', 'IND'],
            'AU': ['Australia', 'AUS'],
            'JP': ['Japan', 'JPN'],
            'CN': ['China', 'CHN'],
            'BR': ['Brazil', 'BRA'],
            'MX': ['Mexico', 'MEX'],
            'IT': ['Italy', 'ITA'],
            'ES': ['Spain', 'ESP'],
            'NL': ['Netherlands', 'Holland', 'NLD'],
            'SE': ['Sweden', 'SWE'],
            'NO': ['Norway', 'NOR'],
            'DK': ['Denmark', 'DNK'],
            'FI': ['Finland', 'FIN'],
            'CH': ['Switzerland', 'CHE'],
            'AT': ['Austria', 'AUT'],
            'BE': ['Belgium', 'BEL'],
            'IE': ['Ireland', 'IRL'],
            'PL': ['Poland', 'POL'],
            'RU': ['Russia', 'RUS'],
            'KR': ['South Korea', 'Korea', 'KOR'],
            'SG': ['Singapore', 'SGP'],
            'MY': ['Malaysia', 'MYS'],
            'TH': ['Thailand', 'THA'],
            'PH': ['Philippines', 'PHL'],
            'ID': ['Indonesia', 'IDN'],
            'VN': ['Vietnam', 'VNM'],
            'ZA': ['South Africa', 'ZAF'],
            'EG': ['Egypt', 'EGY'],
            'SA': ['Saudi Arabia', 'SAU'],
            'AE': ['United Arab Emirates', 'UAE', 'ARE'],
            'TR': ['Turkey', 'TUR'],
            'GR': ['Greece', 'GRC'],
            'PT': ['Portugal', 'PRT'],
            'CZ': ['Czech Republic', 'Czechia', 'CZE'],
            'HU': ['Hungary', 'HUN'],
            'RO': ['Romania', 'ROU'],
            'BG': ['Bulgaria', 'BGR'],
            'HR': ['Croatia', 'HRV'],
            'SI': ['Slovenia', 'SVN'],
            'SK': ['Slovakia', 'SVK'],
            'LT': ['Lithuania', 'LTU'],
            'LV': ['Latvia', 'LVA'],
            'EE': ['Estonia', 'EST'],
            'IS': ['Iceland', 'ISL'],
            'MT': ['Malta', 'MLT'],
            'LU': ['Luxembourg', 'LUX'],
            'CY': ['Cyprus', 'CYP']
        };

        // Check if region matches any variant for the user's country
        const variants = countryVariants[userCountry];
        if (variants && variants.includes(region)) {
            return true;
        }

        return false;
    }

    getContactScore(region, userCountry, hasCountryMatch = true) {
        // If there's a country match, use normal priority
        if (hasCountryMatch) {
            // Use the same matching logic as isCountryMatch
            if (this.isCountryMatch(region, userCountry)) {
                return 3; // Highest priority for matching country
            }

            // International contacts get medium priority
            if (region === 'International') return 2;

            // All others get lowest priority
            return 1;
        } else {
            // If no country match, International gets highest priority
            if (region === 'International') return 3;

            // All others get lower priority
            return 1;
        }
    }

}

// Initialize when DOM ready
document.addEventListener('DOMContentLoaded', () => {
    new ContactGeoSorter();
});